// Cash Flow Forecast — the Overview VoltaGrid tab's version of the project
// cash flow workbook (its "Cash Flow Internal" and "Cash Flow External"
// sheets): one column per month, as cumulative curves and the workbook's rows,
//
//   Monthly Incoming Cash / Cumulative      (External: Est. Monthly Payment
//   Monthly Cost          / Cumulative       Due / Est. Cumulative Payments —
//   Monthly Net Cash      / Cumulative       the client's sheet has no cost)
//
// Nothing here is typed in month by month: both lines are the two curves of
// the Revenue Curve Forecast tab (helpers/revenueCurve.js), read as saved —
//
//   cost     = that tab's Total EAC curve: posted cost by month, then the
//              ETC spread there (Labor and Equipment off the Burn Tool's weeks)
//   incoming = that tab's Revenue curve: posted revenue by month (GL 4xxxx
//              lines, else AR billings), then cost grossed up by the job's
//              EAC margin for the forecast month onward
//
// so the forecast a PM shapes on the Revenue Curve tab is the cash flow the
// client sees here. (The workbook's header block — job facts and billing
// terms — was dropped at Seth's ask; the page above already carries them.)

import React, { useMemo } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
import { pNum } from '../db/company';
import { selectedJobDataAtom, jobCostDetailAtom, jobBillingsAtom, projectCompleteAtom, sideMenuBudgetsAtom, sideMenuCostsAtom } from '../store/company';
import { revenueCurveModel } from '../helpers/revenueCurve';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);


/**
 * The cash flow rows off the Revenue Curve tab's months: income = its Revenue
 * curve, cost = its Total EAC curve, net = the gap, each with a running total.
 * Months before the version's forecast month are actual; the forecast month
 * itself is part actual, part forecast (`partial`); everything after is forecast.
 * @param {object} curve revenueCurveModel()
 */
export function cashFlowFromCurve(curve) {
    const months = (curve && curve.months) || [];
    let cumIncome = 0, cumCost = 0;
    const rows = months.map((m) => {
        const income = pNum(m.revenue);
        const cost = pNum(m.cost);
        cumIncome += income;
        cumCost += cost;
        return {
            key: m.key, label: moment(m.key, 'YYYY-MM').format('MMM-YY'),
            income, cost, net: income - cost, cumIncome, cumCost, cumNet: cumIncome - cumCost,
            forecast: !m.past, partial: !!m.current,
        };
    });
    const totals = { income: cumIncome, cost: cumCost, net: cumIncome - cumCost };
    const actual = rows.filter((r) => !r.forecast);
    return {
        rows, totals,
        dataDate: curve?.dataDate || '',
        revenueSource: curve?.revenueSource || 'none',
        jobProfitPct: curve?.jobProfitPct ?? null,
        incomeActual: actual.reduce((s, r) => s + r.income, 0),
        costActual: actual.reduce((s, r) => s + r.cost, 0),
    };
}

/* ─────────────────────────── formatting ─────────────────────────── */
const money = (v, { dash = true } = {}) => {
    const n = Math.round(pNum(v));
    if (!n) return dash ? '-' : '0';
    const s = Math.abs(n).toLocaleString('en-US');
    return n < 0 ? `(${s})` : s;
};
const fmtDate = (d) => (d && moment(d).isValid() ? moment(d).add(/^\d{4}-\d{2}-\d{2}T00:00:00/.test(String(d)) ? 12 : 0, 'hours').format('M/D/YY') : '—');

/* ─────────────────────────── the chart ─────────────────────────── */
// The cumulative curves on one dollar axis: incoming cash, cost and net cash
// (the gap between the first two). Posted months draw solid, forecast months
// dashed with hollow points. Monthly amounts stay in the table beneath.
const SERIES = {
    income: { color: '#2563eb', label: 'Cumulative Incoming Cash', ext: 'Est. Cumulative Payments' },
    cost: { color: '#d97706', label: 'Cumulative Cost' },
    net: { color: '#15803d', label: 'Cumulative Net Cash' },
};
const compact = (v) => {
    const n = pNum(v), a = Math.abs(n);
    const s = a >= 1e6 ? `${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M` : a >= 1e3 ? `${Math.round(a / 1e3)}k` : String(Math.round(a));
    return n < 0 ? `(${s})` : s;
};
function CashFlowChart({ palette, model, external }) {
    const rows = model.rows;
    const firstF = rows.findIndex((r) => r.forecast);
    // A segment is forecast when it ends in a forecast month; the one that
    // ends in the part-actual data month still draws solid.
    const isForecastSeg = (ctx) => firstF >= 0 && (ctx.p1DataIndex > firstF || (ctx.p1DataIndex === firstF && !rows[firstF].partial));
    const line = (key, data, dash) => ({
        label: external && SERIES[key].ext ? SERIES[key].ext : SERIES[key].label,
        data,
        borderColor: SERIES[key].color,
        backgroundColor: SERIES[key].color,
        borderWidth: 2,
        borderDash: dash,
        tension: 0,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBorderWidth: 2,
        pointBorderColor: SERIES[key].color,
        pointBackgroundColor: rows.map((r) => (r.forecast ? '#fff' : SERIES[key].color)),
        segment: { borderDash: (ctx) => (isForecastSeg(ctx) ? [6, 4] : dash) },
    });
    const datasets = external
        ? [line('income', rows.map((r) => r.cumIncome))]
        : [line('income', rows.map((r) => r.cumIncome)), line('cost', rows.map((r) => r.cumCost)), line('net', rows.map((r) => r.cumNet), [2, 3])];
    const data = { labels: rows.map((r) => r.label), datasets };
    const options = {
        responsive: true, maintainAspectRatio: false, devicePixelRatio: 2,
        interaction: { mode: 'index', intersect: false },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 9 }, color: palette.muted, maxRotation: 0, autoSkip: true } },
            y: { grid: { color: '#f1f5f9' }, border: { display: false }, ticks: { font: { size: 9 }, color: palette.muted, callback: (v) => compact(v), maxTicksLimit: 6 } },
        },
        plugins: {
            legend: { display: datasets.length > 1, position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 2, usePointStyle: false, font: { size: 9.5 }, color: palette.muted, padding: 14 } },
            tooltip: {
                backgroundColor: '#fff', titleColor: palette.text, bodyColor: palette.text, footerColor: palette.muted, borderColor: palette.border, borderWidth: 1, padding: 10, cornerRadius: 6,
                callbacks: {
                    title: (items) => { const r = rows[items[0]?.dataIndex]; return r ? `${r.label}${r.forecast ? (r.partial ? ' — actual + forecast' : ' — forecast') : ''}` : ''; },
                    label: (item) => `${item.dataset.label}: ${money(item.parsed.y, { dash: false })}`,
                    footer: (items) => {
                        const r = rows[items[0]?.dataIndex];
                        if (!r) return '';
                        return external
                            ? [`Month: ${money(r.income, { dash: false })}`]
                            : [`Month — in: ${money(r.income, { dash: false })}   cost: ${money(r.cost, { dash: false })}   net: ${money(r.net, { dash: false })}`];
                    },
                },
            },
        },
    };
    return (
        <div style={{ height: 230, marginBottom: 14 }}>
            <Line data={data} options={options} />
        </div>
    );
}

/* ─────────────────────────── the table ─────────────────────────── */
function CashFlowTable({ palette, model, external }) {
    const INK = '#1e293b';
    const th = { background: INK, color: '#fff', fontSize: 7.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1 };
    const td = { fontSize: 11, color: palette.text, padding: '5px 8px', borderBottom: `1px solid ${palette.border}`, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
    const tdL = { ...td, textAlign: 'left', position: 'sticky', left: 0, background: palette.card, fontWeight: 600, zIndex: 1 };
    const forecastBg = '#f8fafc';
    const lines = external
        ? [
            { key: 'income', label: 'Est. Monthly Payment Due', get: (r) => r.income, total: model.totals.income },
            { key: 'cumIncome', label: 'Est. Cumulative Payments', get: (r) => r.cumIncome, total: model.totals.income },
        ]
        : [
            { key: 'income', label: 'Monthly Incoming Cash', get: (r) => r.income, total: model.totals.income },
            { key: 'cumIncome', label: 'Cumulative Income Cash', get: (r) => r.cumIncome, total: model.totals.income, cum: true },
            { key: 'cost', label: 'Monthly Cost', get: (r) => r.cost, total: model.totals.cost },
            { key: 'cumCost', label: 'Cumulative Cost', get: (r) => r.cumCost, total: model.totals.cost, cum: true },
            { key: 'net', label: 'Monthly Net Cash', get: (r) => r.net, total: model.totals.net, bold: true },
            { key: 'cumNet', label: 'Cumulative Net Cash', get: (r) => r.cumNet, total: model.totals.net, cum: true, bold: true },
        ];
    const tone = (v, bold) => (v < -0.5 ? '#b91c1c' : bold ? palette.text : undefined);
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                    <tr>
                        <th style={thL}>Cash Flow Forecast</th>
                        {model.rows.map((r) => (
                            <th key={r.key} style={{ ...th, color: r.forecast ? '#cbd5e1' : '#fff' }} title={r.forecast ? (r.partial ? 'Actual to date plus forecast' : 'Forecast') : 'Actual'}>{r.label}</th>
                        ))}
                        <th style={{ ...th, borderLeft: '1px solid #475569' }}>Totals</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((ln, i) => {
                        const rowStyle = { fontWeight: ln.bold ? 700 : ln.cum ? 500 : 600, color: ln.cum ? palette.muted : palette.text, borderBottom: ln.cum && i < lines.length - 1 ? `1px solid ${INK}22` : td.borderBottom };
                        return (
                            <tr key={ln.key}>
                                <td style={{ ...tdL, ...rowStyle, color: ln.cum ? palette.muted : palette.text, paddingLeft: ln.cum ? 18 : 8 }}>{ln.label}</td>
                                {model.rows.map((r) => {
                                    const v = ln.get(r);
                                    return <td key={r.key} style={{ ...td, ...rowStyle, background: r.forecast ? forecastBg : 'transparent', color: tone(v, ln.bold) || rowStyle.color }}>{money(v)}</td>;
                                })}
                                <td style={{ ...td, ...rowStyle, fontWeight: 700, borderLeft: `1px solid ${palette.border}`, color: tone(ln.total, true) || palette.text }}>{money(ln.total)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/**
 * The external cash flow table as array-of-arrays for an Excel sheet: one
 * column per month plus Totals, the two client-facing rows, and a basis line
 * saying which months are actual vs forecast.
 * @param {ReturnType<typeof cashFlowFromCurve>} model
 */
export function cashFlowSheetRows(model, { asOf = '' } = {}) {
    const rows = (model && model.rows) || [];
    const header = ['Cash Flow Forecast', ...rows.map((r) => r.label), 'Totals'];
    const basis = ['Basis', ...rows.map((r) => (r.forecast ? (r.partial ? 'Actual + Forecast' : 'Forecast') : 'Actual')), ''];
    const line = (label, get, total) => [label, ...rows.map((r) => Math.round(get(r))), Math.round(total)];
    return [
        [`Project Cash Flow Forecast${asOf ? ` - as of ${asOf}` : ''}`],
        [],
        header,
        basis,
        line('Est. Monthly Payment Due', (r) => r.income, model?.totals?.income || 0),
        line('Est. Cumulative Payments', (r) => r.cumIncome, model?.totals?.income || 0),
    ];
}

/* ─────────────────────────── the section ─────────────────────────── */
function VoltaGridCashFlow({ palette, versionInfo, jobUserData, selectedJob, exportRef }) {
    const [job] = useAtom(selectedJobDataAtom);
    const [costRows] = useAtom(jobCostDetailAtom);
    const [jdeBillings] = useAtom(jobBillingsAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);
    const [smBudgets] = useAtom(sideMenuBudgetsAtom);
    const [smCosts] = useAtom(sideMenuCostsAtom);

    // External (client-facing) view only — the internal cost view was dropped from the overview on 10/7.
    const external = true;

    const ready = Array.isArray(costRows) && Array.isArray(jobUserData);
    const model = useMemo(() => (ready ? cashFlowFromCurve(revenueCurveModel({
        userData: jobUserData, jobCostDetail: costRows, jobBillings: jdeBillings,
        selectedJobData: job, completionDateForecast: projectComplete, versionInfo, smBudgets, smCosts,
    })) : null), [ready, jobUserData, costRows, jdeBillings, job, projectComplete, versionInfo, smBudgets, smCosts]);

    const asOf = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).format('M/D/YY') : moment().format('M/D/YY');

    // Hand the parent a way to pull this table as sheet rows (the title bar's
    // Export button bundles it with the page PDF).
    React.useEffect(() => {
        if (!exportRef) return undefined;
        exportRef.current = () => (model && model.rows.length ? cashFlowSheetRows(model, { asOf }) : null);
        return () => { exportRef.current = null; };
    }, [exportRef, model, asOf]);

    return (
        <div className="vg-cashflow-section" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>Project Cash Flow Forecast</h3>
                    <span style={{ fontSize: 10, color: palette.muted }}>as of {asOf}</span>
                </div>
            </div>

            {!ready ? (
                <div style={{ fontSize: 11, color: palette.muted }}>Loading the cost history and the Revenue Curve…</div>
            ) : !model || !model.rows.length ? (
                <div style={{ fontSize: 11, color: palette.muted }}>No cost has posted and no start date is set for this job.</div>
            ) : (
                <>
                    <CashFlowChart palette={palette} model={model} external={external} />
                    <CashFlowTable palette={palette} model={model} external={external} />
                    <div style={{ fontSize: 9.5, color: palette.muted, lineHeight: 1.5, marginTop: 8 }}>
                        Both lines are the Revenue Curve Forecast tab's curves. Months in white are posted{model.dataDate ? ` (through ${fmtDate(model.dataDate)})` : ''}; shaded months are the forecast spread on that tab
                        {external ? '.' : ' (Labor and Equipment from the Burn Tool, the rest as entered).'}
                        {' '}{external ? 'Payments' : 'Incoming cash'}: {model.revenueSource === 'gl' ? 'posted revenue by month' : model.revenueSource === 'billings' ? 'JDE AR invoices by GL month' : 'nothing posted yet'}
                        {model.jobProfitPct != null ? `, then each forecast month's cost at the job's ${(model.jobProfitPct * 100).toFixed(1)}% margin.` : '; forecast months carry no revenue until the job has a contract value.'}
                    </div>
                </>
            )}
        </div>
    );
}

export default VoltaGridCashFlow;
